import type { IconName } from '@w7/ui'
import type { KcMfaAction } from '@/auth/oidc'

export type MethodStatus = 'ready' | 'soon' | 'blocked'

export interface MfaMethod {
  id: string
  icon: IconName
  title: string
  /** Long copy for enroll wizard. */
  body: string
  /** One short line on the cover card overlay. */
  blurb: string
  status: MethodStatus
  statusLabel: string
  actionLabel?: string
  kcAction?: KcMfaAction
  steps?: string[]
  image: string
  /**
   * When false, keep for deep-link wizards / canonical mapping but do not show
   * on the security hub (WebAuthn shortcuts that share “This device”).
   */
  hubVisible?: boolean
}

/** Catalog for /settings/security — enroll via branded wizards + Keycloak AIA. */
export const MFA_METHODS: MfaMethod[] = [
  {
    id: 'totp',
    icon: 'lock',
    title: 'Authenticator app',
    blurb: '6-digit codes from your phone app',
    body: 'A rotating one-time code from Google Authenticator, Authy, 1Password, or Microsoft Authenticator — entered after your password.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Set up',
    kcAction: 'CONFIGURE_TOTP',
    image: '/auth/mfa/mfa-totp.png',
    steps: [
      'Open your authenticator app on your phone.',
      'Continue — you will scan a QR code (or enter a secret) on the secure enroll step.',
      'Confirm with a 6-digit code. Next login will ask for that code after your password.',
      'Note: Keycloak usually keeps one authenticator. “Add another” replaces the existing app, it does not keep two OTP devices. Use Passkey if you need a second factor type.',
    ],
  },
  {
    id: 'passkey',
    icon: 'shield',
    title: 'Passkey',
    blurb: 'Touch ID, fingerprint, Face ID, or USB key',
    body: 'Same pattern as Google and GitHub: one “Passkey” option — not separate Fingerprint / This device cards. Works on Mac (Touch ID), Android (fingerprint), Windows Hello, Face ID, or a USB/NFC security key. The browser asks to create a passkey; you approve with biometrics or the key. Keycloak stores one credential; only this card shows Activated.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Register',
    kcAction: 'webauthn-register',
    image: '/auth/mfa/mfa-passkey.png',
    steps: [
      'Use this device’s unlock (Touch ID, fingerprint, face, PIN) or plug in a security key.',
      'Continue — the browser will say “passkey” on every major OS (that wording is normal).',
      'Your biometric stays on the device. Remove on this card turns the factor off.',
    ],
  },
  {
    id: 'fingerprint',
    hubVisible: false,
    icon: 'user',
    title: 'Fingerprint',
    blurb: 'Same as Passkey — Touch ID or phone fingerprint',
    body: 'Deep-link shortcut into the same enroll as Passkey. On Mac use Touch ID; on Android use fingerprint. Success always Activates Passkey — never a separate Fingerprint pill.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Register',
    kcAction: 'webauthn-register',
    image: '/auth/mfa/mfa-fingerprint.png',
    steps: [
      'Enroll a fingerprint / Touch ID in the OS if needed.',
      'Continue — when the browser says passkey, approve with your finger.',
      'Return to the hub: only Passkey shows Activated.',
    ],
  },
  {
    id: 'security-key',
    hubVisible: false,
    icon: 'lock',
    title: 'Security key',
    blurb: 'Same as Passkey — USB or NFC key',
    body: 'Deep-link shortcut into the same enroll as Passkey. Choose your hardware key when the browser prompts. Success Activates Passkey only.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Add key',
    kcAction: 'webauthn-register',
    image: '/auth/mfa/mfa-security-key.png',
    steps: [
      'Have your USB or NFC security key ready.',
      'Continue — choose security key when prompted.',
      'Return to the hub: only Passkey shows Activated.',
    ],
  },
  {
    id: 'recovery',
    icon: 'copy',
    title: 'Recovery codes',
    blurb: 'Backup codes if you lose your phone',
    body: 'One-time backup codes if you lose your phone or key. Store them offline.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Generate',
    kcAction: 'CONFIGURE_RECOVERY_AUTHN_CODES',
    image: '/auth/mfa/mfa-recovery.png',
    steps: [
      'Continue to generate one-time recovery codes.',
      'Copy or print them and store offline — each code works once.',
      'Anyone with a code can complete sign-in once; treat them like passwords.',
    ],
  },
  {
    id: 'email',
    icon: 'info',
    title: 'Email code',
    blurb: 'Code sent to your email',
    body: 'A code sent to your account email after password. Needs Keycloak SMTP before it can go live.',
    status: 'soon',
    statusLabel: 'Needs SMTP',
    image: '/auth/mfa/mfa-email.png',
  },
  {
    id: 'sms',
    icon: 'live',
    title: 'SMS code',
    blurb: 'Code texted to your phone',
    body: 'A code texted to your phone. Needs an owner-approved SMS provider (paid) — not enabled yet.',
    status: 'blocked',
    statusLabel: 'Needs approval',
    image: '/auth/mfa/mfa-sms.png',
  },
]

/** Cards shown on /settings/security (excludes WebAuthn shortcut deep-links). */
export const MFA_HUB_METHODS: MfaMethod[] = MFA_METHODS.filter((m) => m.hubVisible !== false)

export function getMfaMethod(id: string | undefined): MfaMethod | undefined {
  if (!id) return undefined
  return MFA_METHODS.find((m) => m.id === id)
}
